import { Button } from '@/components/ui/button';
import DeleteConfirmDialog from '@/components/ui/dialog/delete-confirm-dialog';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { coRegistrantStore, customerAddressStore } from '@/models/stores';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MinusIcon } from 'lucide-react';
import { useCallback, useState } from 'react';

const CoRegistrantDeleteButton = ({
  dealId,
  coRegistrantId,
}: {
  dealId: Deal['id'];
  coRegistrantId: NonNullable<Deal['co_registrant']>['id'];
}) => {
  const queryClient = useQueryClient();
  const deleteCoRegistrantMutation = useMutation({
    onMutate: async () => {
      coRegistrantStore.remove({ id: coRegistrantId, dealId });
      const coRegistrant = coRegistrantStore.getById(coRegistrantId);
      const addressId = coRegistrant?.addresses[0]?.id;
      if (addressId) {
        customerAddressStore.remove({
          id: addressId,
          customerId: coRegistrantId,
        });
      }
    },
    mutationFn: async (): Promise<void> => {
      await pdkAxios
        .post(`/deals/${dealId}/co-registrant/${coRegistrantId}/delete`)
        .catch((e) => {
          console.error('Error deleting co-registrant', e);
          throw e;
        })
        .catch((e) => {
          console.error('Error deleting co-registrant', e);
          throw e;
        });

      return;
    },
    onSuccess: () => {
      setDialogIsOpen(false);
    },
    onError: (error, variables) => {
      console.error('Error deleting co-registrant', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });
  const onConfirm = () => deleteCoRegistrantMutation.mutate();
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const onOpen = useCallback(() => {
    setDialogIsOpen(true);
  }, [setDialogIsOpen]);
  const onClose = useCallback(() => {
    setDialogIsOpen(false);
  }, [setDialogIsOpen]);

  return (
    <DeleteConfirmDialog
      onConfirm={onConfirm}
      dialogIsOpen={dialogIsOpen}
      onOpen={onOpen}
      onClose={onClose}
      title="Delete co-registrant"
      description="Please confirm you would like to delete the co-registrant."
    >
      <Button
        variant="ghost"
        className="p-0 h-fit w-fit text-gray-500 dark:text-white"
      >
        <MinusIcon className="w-2 h-2 dark:text-white" />
      </Button>
    </DeleteConfirmDialog>
  );
};

export default CoRegistrantDeleteButton;
